import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

interface EditorSectionProps {
	title: string;
	subtitle?: string;
	icon?: LucideIcon;
	/** Right-aligned slot in the head (e.g. a duration pill). */
	right?: ReactNode;
	children: ReactNode;
}

/** Card wrapper shared by every editor section (06-editor-spec.md card pattern). */
export function EditorSection({ title, subtitle, icon: Icon, right, children }: EditorSectionProps) {
	return (
		<section className="rounded-lg border border-border bg-surface p-5">
			<div className="mb-4 flex items-start justify-between gap-4 border-b border-border-subtle pb-3">
				<div className="flex items-start gap-2">
					{Icon && <Icon size={16} className="mt-0.5 text-foreground-muted" />}
					<div>
						<h3 className="text-md font-semibold text-foreground">{title}</h3>
						{subtitle && <p className="mt-0.5 text-xs text-foreground-muted">{subtitle}</p>}
					</div>
				</div>
				{right}
			</div>
			{children}
		</section>
	);
}

interface FieldProps {
	label?: string;
	help?: string;
	error?: string;
	htmlFor?: string;
	className?: string;
	children: ReactNode;
}

/** Label + control + help/error wrapper for form fields. */
export function Field({ label, help, error, htmlFor, className, children }: FieldProps) {
	return (
		<div className={cn('flex flex-col gap-1.5', className)}>
			{label && (
				<Label htmlFor={htmlFor} className="text-sm font-medium text-foreground">
					{label}
				</Label>
			)}
			{children}
			{error ? <p className="text-xs text-danger-fg">{error}</p> : help ? <p className="text-xs text-foreground-muted">{help}</p> : null}
		</div>
	);
}
